import { useMemo, useState } from "react";
import { ComparisonResults } from "@/components/career-path/ComparisonResults";
import { ErrorState } from "@/components/shared/error-state";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { ROLE_NOT_FOUND, useRoleComparison } from "@/hooks/use-career-path";
import { useMyPerson, usePersonRoles } from "@/hooks/use-persons";
import { isCurrentAssignment } from "@/lib/role-dates";

interface CompareSeed {
  /** Pre-select this role on the starting side (e.g. from a person profile). */
  startRoleId?: string;
  /** Whose primary role the seed is — names the hint under the picker. */
  startPersonName?: string;
  /** Pre-select this role on the target side (e.g. the Role Catalog's per-row button). */
  targetRoleId?: string;
}

function Pickers({
  personId,
  startRoleId,
  startPersonName,
  targetRoleId,
}: CompareSeed & { personId: string | null }) {
  const { data: roles, isLoading } = usePersonRoles(personId);
  const [pickedFromId, setPickedFromId] = useState<string | null>(null);
  const [toId, setToId] = useState<string | null>(targetRoleId ?? null);

  const currentRoles = useMemo(
    () => (roles ?? []).filter((r) => isCurrentAssignment(r.endDate)),
    [roles],
  );
  // A seed from the URL (a profile's "Compare roles" button passing that
  // person's primary role) wins; otherwise default to the viewer's own primary
  // current role (falling back to their first assignment). The picker itself is
  // open to any role in the company — the default is a convenience, not a lock.
  // No linked person record, no assignments, or a roles-load failure all
  // degrade the same way: no default, pick the starting role yourself.
  const defaultFromId =
    startRoleId ?? currentRoles.find((r) => r.isPrimary)?.roleId ?? currentRoles[0]?.roleId ?? null;
  // The same role on both sides is a legitimate comparison — it lines up two
  // career levels of one role — so the default stands even when it matches the
  // seeded target (e.g. hitting the catalog's compare button on your own role).
  const fromId = pickedFromId ?? defaultFromId;

  const comparison = useRoleComparison(fromId, toId);

  // Hold for the default before first paint so the starting-role picker
  // doesn't flash empty and then fill in.
  if (personId !== null && isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <div className="text-sm font-medium">Starting role</div>
          <EntityCombobox
            entity="roles"
            value={fromId}
            onChange={setPickedFromId}
            filters={{ status: "active" }}
            placeholder="Select a starting role…"
            searchPlaceholder="Search roles…"
          />
          {fromId !== null && pickedFromId === null && (
            <p className="text-xs text-muted-foreground">
              {startRoleId
                ? startPersonName
                  ? `${startPersonName}'s primary role.`
                  : "Preselected role."
                : "Your primary role."}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <div className="text-sm font-medium">Target role</div>
          <EntityCombobox
            entity="roles"
            value={toId}
            onChange={setToId}
            filters={{ status: "active" }}
            placeholder="Select a target role…"
            searchPlaceholder="Search roles…"
          />
        </div>
      </div>

      {!fromId || !toId ? (
        <p className="text-sm text-muted-foreground">
          Pick a starting and a target role to see which skills and competencies they share, and
          which you'd gain or leave behind. Pick the same role on both sides to compare two of its
          career levels.
        </p>
      ) : comparison.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : comparison.isError ? (
        comparison.error?.message === ROLE_NOT_FOUND ? (
          <p className="text-sm text-muted-foreground">
            One of these roles no longer exists — pick a different role.
          </p>
        ) : (
          <ErrorState message="Failed to compare roles" onRetry={() => comparison.refetch()} />
        )
      ) : comparison.data ? (
        <ComparisonResults comparison={comparison.data} />
      ) : null}
    </div>
  );
}

/**
 * Role-to-role career-path comparison (EPIC-029 / WI-063): pick any two roles
 * in the company and see the overlapping / new / no-longer-needed
 * requirements. The starting role defaults to the seed passed in the URL (a
 * person profile's "Compare roles" button sends that person's primary role),
 * else the signed-in employee's own primary current role when their account is
 * linked to a person.
 *
 * Picking the same role on both sides is allowed and useful: the requirement
 * sets are then identical, and the comparison becomes one role's expectations
 * at two career levels.
 */
export function CareerPathCompare({ startRoleId, startPersonName, targetRoleId }: CompareSeed) {
  const { data: person, isLoading } = useMyPerson();

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <Pickers
      personId={person?.id ?? null}
      startRoleId={startRoleId}
      startPersonName={startPersonName}
      targetRoleId={targetRoleId}
    />
  );
}
